import { useRegenerateAssistantResponse } from '@modules/oracle/hooks/useRegenerateAssistantResponse';
import { useStopAssistantResponse } from '@modules/oracle/hooks/useStopAssistantResponse';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { updatePromptByID } from '@modules/oracle/utils';
import { RegenerateAssistantResponseStepInput } from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { useCallback, useRef } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface RegenerateResponseParams {
  question?: string;
  step?: RegenerateAssistantResponseStepInput;
  promptID: string;
  onRegenerate?: (newPromptID: string) => void;
}

export const useRegenerateWisdomResponse = () => {
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const promptByID = useOracleSearchStore(({ data }) => data.promptByID);
  const currentPromptID = useOracleSearchStore(
    ({ data }) => data.currentPromptID,
  );

  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const dependencies = {
    promptByID,
    userID,
    tenantID,
    currentPromptID,
  };

  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const [
    regenerateAssistantResponseQuery,
    { isLoading: isRegenerateResponseInProgress },
  ] = useRegenerateAssistantResponse();

  const [stopAssistantResponseQuery, { isLoading: isStopResponseInProgress }] =
    useStopAssistantResponse();

  const stopResponseIfRequired = useCallback(
    async (promptID: string) => {
      const promptToRegenerate = dependenciesRef.current.promptByID?.[promptID];

      if (!promptToRegenerate) {
        return;
      }

      const isResponsePending =
        !promptToRegenerate.endOfResponse && !promptToRegenerate.error;

      const shouldStopResponse = !!promptID && isResponsePending;

      if (!shouldStopResponse) {
        return shouldStopResponse;
      }

      const updatedPromptByID = cloneDeep(
        dependenciesRef.current.promptByID ?? {},
      );

      if (updatedPromptByID) {
        updatedPromptByID[promptID].isResponseHalted = true;
        updatedPromptByID[promptID].endOfResponse = true;
      }

      dispatch({
        type: OracleSearchActionType.SET_DATA,
        payload: {
          promptByID: updatedPromptByID,
        },
      });

      await stopAssistantResponseQuery(promptID);

      return shouldStopResponse;
    },
    [dispatch, stopAssistantResponseQuery],
  );

  const regenerateResponse = useCallback(
    async ({ question, step, promptID }: RegenerateResponseParams) => {
      const resp = await regenerateAssistantResponseQuery({
        promptID,
        question,
        step,
      });

      const newPromptID = resp?.data?.regenerateAssistantResponse?.responseID;

      if (newPromptID) {
        const updatedPromptByID = updatePromptByID({
          newPromptID,
          promptByID: dependenciesRef.current.promptByID ?? {},
          editedFromResponseID: promptID,
          editedStepID: step?.stepID,
          newPromptData: question
            ? {
                question,
                userID: dependenciesRef.current.userID,
                tenantID: dependenciesRef.current.tenantID,
                previousResponseID: dependenciesRef.current.promptByID
                  ? undefined
                  : dependenciesRef.current.currentPromptID,
              }
            : undefined,
        });

        dispatch({
          type: OracleSearchActionType.SET_DATA,
          payload: {
            promptByID: updatedPromptByID,
            currentPromptID: newPromptID,
          },
        });
      }
      return newPromptID;
    },
    [dispatch, regenerateAssistantResponseQuery],
  );

  const regenerateResponseWrapper = useCallback(
    async ({
      question,
      step,
      promptID,
      onRegenerate,
    }: RegenerateResponseParams) => {
      const shouldStopResponse = await stopResponseIfRequired(promptID);

      setTimeout(
        async () => {
          const newPromptID = await regenerateResponse({
            question,
            step,
            promptID,
          });

          newPromptID && onRegenerate?.(newPromptID);
        },
        shouldStopResponse ? 1000 : 0,
      ); // Delay to allow the response to stop before regenerating new response
    },
    [regenerateResponse, stopResponseIfRequired],
  );

  return [
    regenerateResponseWrapper,
    { isLoading: isRegenerateResponseInProgress || isStopResponseInProgress },
  ] as const;
};
